import { endOfMonth, format, startOfMonth } from "date-fns";
import * as XLSX from "exceljs";
import { COUNTRIES_SHORT } from "../enums/Countries";
import { TAX_CONDITIONS } from "../enums/TaxConditions";
import { getMonthName, toDateOnly } from "./DateTimeUtils";

const PROVINCE_NAME_MAP = {
  "CIUDAD AUTONOMA BUENOS AIRES": "CIUDAD AUTÓNOMA DE BUENOS AIRES",
  CORDOBA: "CÓRDOBA",
  "ENTRE RIOS": "ENTRE RÍOS",
  TUCUMAN: "TUCUMÁN",
  NEUQUEN: "NEUQUÉN",
  "RIO NEGRO": "RÍO NEGRO",
};

const mapProvince = (province) =>
  PROVINCE_NAME_MAP[province] ?? province ?? "BUENOS AIRES";

const downloadFile = (buffer, restaurantName) => {
  // Trigger file download
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${restaurantName}.xlsx`;
  link.click();
};

export const billingListDefaultValues = () => ({
  country: [COUNTRIES_SHORT.AR],
  paidDateFrom: startOfMonth(new Date()).toISOString(),
  paidDateTo: endOfMonth(new Date()).toISOString(),
});

const formatFileName = (service, paidDateFrom, paidDateTo) =>
  `${service}_${paidDateFrom ? format(new Date(paidDateFrom), "yyyy-MM-dd") : ""}_${paidDateTo ? format(new Date(paidDateTo), "yyyy-MM-dd") : ""}`;

export const exportToExcelFacturante = async ({
  billingInfo,
  paidDateFrom,
  paidDateTo,
}) => {
  const arBillingWithData = billingInfo.filter(
    (b) => b.restaurantCountry === COUNTRIES_SHORT.AR && b.billingData
  );
  if (!arBillingWithData.length) return;

  const paidDateToDate = toDateOnly(paidDateTo);
  const paidDateFromDate = toDateOnly(paidDateFrom);
  const monthName = getMonthName(paidDateToDate);
  const year = paidDateToDate.getFullYear();
  const detalle = `Servicios de Publicidad - ${monthName} ${year}`;

  const workbook = new XLSX.Workbook();
  const worksheet = workbook.addWorksheet("COMPROBANTES");

  const DATE_FORMAT = "d/m/yy";

  worksheet.columns = [
    { header: "RAZONSOCIAL", key: "razonsocial", width: 30 },
    { header: "NRODOCUMENTO", key: "nrodocumento", width: 15 },
    { header: "TIPODOCUMENTO", key: "tipodocumento", width: 15 },
    { header: "DIRECCIONFISCAL", key: "direccionfiscal", width: 30 },
    { header: "PROVINCIA", key: "provincia", width: 30 },
    { header: "LOCALIDAD", key: "localidad", width: 30 },
    { header: "CODIGOPOSTAL", key: "codigopostal", width: 15 },
    { header: "PERCIBEIVA", key: "percibeiva", width: 15 },
    { header: "PERCIBEIIBB", key: "percibeiibb", width: 15 },
    {
      header: "TRATAMIENTOIMPOSITIVO",
      key: "tratamientoimpositivo",
      width: 25,
    },
    { header: "ENVIARCOMPROBANTE", key: "enviarcomprobante", width: 15 },
    { header: "MAILFACTURACION", key: "mailfacturacion", width: 30 },
    { header: "CONTACTO", key: "contacto", width: 15 },
    { header: "TELEFONO", key: "telefono", width: 15 },
    { header: "CONVENIOMULTILATERAL", key: "conveniomultilateral", width: 15 },
    { header: "ENVIARBOTONPAGO", key: "enviarbotonpago", width: 15 },
    { header: "NUMEROCOMPROBANTE", key: "numerocomprobante", width: 20 },
    { header: "FECHAHORA", key: "fechahora", width: 12 },
    { header: "TIPOCOMPROBANTE", key: "tipocomprobante", width: 15 },
    { header: "OBSERVACIONES", key: "observaciones", width: 15 },
    { header: "DESCUENTO", key: "descuento", width: 15 },
    { header: "PERCEPCIONIVA", key: "percepcioniva", width: 15 },
    { header: "PERCEPCIONIIBB", key: "percepcioniibb", width: 15 },
    { header: "ORDENCOMPRA", key: "ordencompra", width: 15 },
    { header: "REMITO", key: "remito", width: 15 },
    {
      header: "IMPORTEIMPUESTOSINTERNOS",
      key: "importeimpuestosinternos",
      width: 15,
    },
    {
      header: "IMPORTEPERCEPCIONESMUNIC",
      key: "importepercepcionesmunic",
      width: 15,
    },
    { header: "MONEDA", key: "moneda", width: 8 },
    { header: "TIPODECAMBIO", key: "tipodecambio", width: 10 },
    { header: "CONDICIONVENTA", key: "condicionventa", width: 12 },
    { header: "PRODUCTOSERVICIO", key: "productoservicio", width: 15 },
    { header: "FECHAVENCIMIENTO", key: "fechavencimiento", width: 12 },
    { header: "FECHAINICIOABONO", key: "fechainicioabono", width: 12 },
    { header: "FECHAFINABONO", key: "fechafinabono", width: 12 },
    { header: "CANTIDAD", key: "cantidad", width: 8 },
    { header: "DETALLE", key: "detalle", width: 40 },
    { header: "CODIGO", key: "codigo", width: 40 },
    { header: "BONIFICACION", key: "bonificacion", width: 40 },
    { header: "IVA", key: "iva", width: 8 },
    { header: "PRECIOUNITARIO", key: "preciounitario", width: 15 },
    { header: "CBU", key: "cbu", width: 15 },
    { header: "ESANULACION", key: "esanulacion", width: 15 },
    { header: "TRANSFERENCIA", key: "transferencia", width: 15 },
    {
      header: "PAGO MONEDA EXTRANJERA",
      key: "pagomonedaextranjera",
      width: 30,
    },
  ];

  arBillingWithData.forEach((record, index) => {
    const { billingData, netMorfyAmount, totalServiceCostWithoutTaxesAmount } =
      record;
    const invoiceNumber = String(index + 1).padStart(8, "0");
    const price = netMorfyAmount + totalServiceCostWithoutTaxesAmount;

    if (price <= 0) return;

    const row = worksheet.addRow({
      razonsocial: billingData.name,
      nrodocumento: billingData.cuit,
      tipodocumento: billingData.docType,
      direccionfiscal: billingData.address || "NO INFORMADA",
      provincia: mapProvince(billingData.province),
      localidad: mapProvince(billingData.province),
      tratamientoimpositivo: billingData.taxCondition,
      enviarcomprobante: billingData.email ? "S" : "N",
      mailfacturacion: billingData.email || "",
      conveniomultilateral: "N",
      enviarbotonpago: "N",
      numerocomprobante: `00002-${invoiceNumber}`,
      fechahora: format(paidDateToDate, "dd/MM/yyyy"),
      tipocomprobante: [
        TAX_CONDITIONS.RESPONSABLE_INSCRIPTO,
        TAX_CONDITIONS.MONOTRIBUTISTA,
      ].includes(billingData.taxCondition)
        ? "FA"
        : "FB",
      moneda: 2,
      tipodecambio: 1,
      condicionventa: 2,
      productoservicio: 2,
      fechavencimiento: format(paidDateToDate, "dd/MM/yyyy"),
      fechainicioabono: format(paidDateFromDate, "dd/MM/yyyy"),
      fechafinabono: format(paidDateToDate, "dd/MM/yyyy"),
      cantidad: 1,
      detalle,
      iva: 21,
      preciounitario: price,
    });

    [
      "fechahora",
      "fechavencimiento",
      "fechainicioabono",
      "fechafinabono",
    ].forEach((key) => {
      row.getCell(key).numFmt = DATE_FORMAT;
    });
  });

  const asociadosSheet = workbook.addWorksheet("ASOCIADOS");
  asociadosSheet.columns = [
    { header: "TIPOCOMPROBANTE", key: "tipocomprobante", width: 15 },
    { header: "NUMEROCOMPROBANTE", key: "numerocomprobante", width: 20 },
    {
      header: "TIPOCOMPROBANTEASOCIADO",
      key: "tipocomprobanteasociado",
      width: 22,
    },
    {
      header: "NUMEROCOMPROBANTEASOCIADO",
      key: "numerocomprobanteasociado",
      width: 24,
    },
    {
      header: "FECHACOMPROBANTEASOCIADO",
      key: "fechacomprobanteasociado",
      width: 22,
    },
  ];

  const fileName = formatFileName("facturante", paidDateFrom, paidDateTo);
  const buffer = await workbook.xlsx.writeBuffer();
  downloadFile(buffer, fileName);
};
